import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useFirmMode } from '../contexts/FirmModeContext';
import LanguageSwitcher from './LanguageSwitcher';
import YimnakNavigationLauncher from './YimnakNavigationLauncher';
import YimnakBrandMark from './YimnakBrandMark';
import './Header.css';

const Header = () => {
  const { t, dir, isRTL } = useLanguage();
  const { user, isAuthenticated, logout } = useAuth();
  const { isFirmMode, clearFirmMode } = useFirmMode();
  const navigate = useNavigate();
  const [isBrowserOnline, setIsBrowserOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  const isGuestUser = !!user?.isGuest;
  const isRegisteredUser = isAuthenticated && user && !isGuestUser;

  const displayName = (user?.name || user?.email || '').trim();
  const nameInitial = displayName
    ? displayName.charAt(0).toUpperCase()
    : '?';

  useEffect(() => {
    const on = () => setIsBrowserOnline(true);
    const off = () => setIsBrowserOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleClearFirmMode = () => {
    clearFirmMode();
    navigate('/');
  };
  
  return (
    <header className={`header ${isRTL ? 'rtl' : 'ltr'}`} dir={dir}>
      <div className="header-container">
        <div className="logo">
          <Link to={isAuthenticated ? '/home' : '/'} className="logo-link">
            <YimnakBrandMark variant="default" size={28} decorative className="logo-mark" />
            <h1>{t('brand.name')}</h1>
          </Link>
        </div>

        <div className="header-actions">
          <YimnakNavigationLauncher variant="workspace" />

          <LanguageSwitcher />

          {isRegisteredUser && (
            <div className="user-menu user-menu--registered">
              <div className="user-menu-main">
                <span className="user-avatar" aria-hidden="true">
                  {nameInitial}
                </span>
                <div className="user-details">
                  <span className="user-name" title={displayName}>
                    {displayName}
                  </span>
                  <span
                    className={`user-status-line ${isBrowserOnline ? 'is-online' : 'is-offline'}`}
                  >
                    {isBrowserOnline ? t('header.onlineNow') : t('header.offline')}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="logout-btn logout-btn--account"
                title={t('logout')}
              >
                {t('logout')}
              </button>
            </div>
          )}
          {isGuestUser && (
            <div className="user-menu user-menu--guest">
              <span className="guest-badge">{t('header.guestBadge')}</span>
              <Link to="/login" className="header-guest-login-btn">
                {t('nav.login')}
              </Link>
            </div>
          )}
          {isFirmMode && !isAuthenticated && (
            <div className="user-menu">
              <span className="user-name">Firm mode</span>
              <button
                type="button"
                onClick={handleClearFirmMode}
                className="logout-btn"
                title="Exit firm mode"
              >
                Exit firm mode
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
